import React, { useState } from 'react';
import { usePOSStore } from '../../../stores/pos.store';
import { Button } from '../../common/Button';
import { useToast } from '../../common/ToastProvider';
import { CheckCircle2, User, MoreVertical, Banknote, CreditCard, Gift, MoreHorizontal, FileText, BarChart3, RotateCcw } from 'lucide-react';
import { usePaymentMethods, useBaseCurrency, useStaff, useActiveRate } from '../../../db/hooks/useLookupData';
import { formatLocal } from '../../../lib/currency';
import { VoidTransactionDrawer } from '../AnularVentaDrawer';

interface POSSummaryStepProps {
  lastTransactionId?: string | null;
}

export function POSSummaryStep({ lastTransactionId }: POSSummaryStepProps) {
  const { cartItems, paymentEntries, customer, discount, tip, tipOperatorId, close, reset } = usePOSStore();
  const { paymentMethods = [] } = usePaymentMethods();
  const { staff = [] } = useStaff();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const { activeRate } = useActiveRate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<'details' | 'activity'>('details');
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const [isVoidDrawerOpen, setIsVoidDrawerOpen] = useState(false);
  const [isVoided, setIsVoided] = useState(false);

  const rateKey = baseCurrency.toLowerCase() as 'usd' | 'eur' | 'ves';
  const bcvRate = rateKey === 'eur' ? (activeRate?.eur || 1) : rateKey === 'usd' ? (activeRate?.usd || 1) : 1;

  const subtotal = cartItems.reduce((acc, item) => acc + item.unitPrice * item.quantity, 0);
  const totalCharged = Math.max(0, subtotal - (discount || 0) + (tip || 0));
  const tipOperator = staff.find((p) => p.id === tipOperatorId);
  const dateStr = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(new Date());

  const getIconForMethod = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('efectivo')) return Banknote;
    if (n.includes('tarjeta') || n.includes('zelle')) return CreditCard;
    if (n.includes('regalo') || n.includes('gift')) return Gift;
    return MoreHorizontal;
  };

  return (
    <div className="flex flex-col h-full animate-fade-in relative overflow-hidden bg-surface">
      
      {/* ── Scrollable Content Area ── */}
      <div className="flex-1 overflow-y-auto p-6 pb-28 md:pb-6 flex flex-col md:flex-row gap-6 custom-scrollbar">
        
        {/* ── Left Column: Summary and Tabs ── */}
        <div className="w-full md:w-64 shrink-0 flex flex-col gap-4">
          <div className="bg-surface-secondary backdrop-blur-md border border-border p-5 rounded-2xl shadow-xs">
            {isVoided ? (
              <div className="flex items-center gap-3 font-bold text-lg text-rose-500 mb-2">
                <RotateCcw className="w-6 h-6 text-rose-500" /> ¡Venta Anulada!
              </div>
            ) : (
              <div className="flex items-center gap-3 font-bold text-lg text-text-primary mb-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" /> ¡Completada!
              </div>
            )}
            <p className="text-sm text-text-secondary">
              {dateStr}
            </p>
          </div>
          
          <div className="bg-surface-secondary backdrop-blur-md border border-border rounded-2xl flex flex-col p-3 gap-2">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer ${
                activeTab === 'details'
                  ? 'bg-brand-orange text-white shadow-md shadow-brand-orange/20'
                  : 'text-text-secondary hover:text-white hover:bg-white/5'
              }`}
            >
              <FileText className="w-4 h-4 shrink-0" /> Detalles
            </button>

            <button
              onClick={() => setActiveTab('activity')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer ${
                activeTab === 'activity'
                  ? 'bg-brand-orange text-white shadow-md shadow-brand-orange/20'
                  : 'text-text-secondary hover:text-white hover:bg-white/5'
              }`}
            >
              <BarChart3 className="w-4 h-4 shrink-0" /> Actividad
            </button>
          </div>
        </div>

        {/* ── Right Column: Receipt ── */}
        <div className="flex-1 flex flex-col gap-6">
          
          {/* Header actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xl sm:text-3xl font-black text-text-primary min-w-0 truncate">Venta</h2>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => toast.info('Volver a reservar en desarrollo', 'Próximamente')} className="hover:border-brand-orange hover:text-brand-orange transition-colors text-xs font-bold">Volver a reservar</Button>
              
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setIsOptionsMenuOpen((prev) => !prev)}
                  className="px-3 hover:bg-white/5 transition-colors cursor-pointer"
                  aria-label="Más opciones"
                >
                  <MoreVertical className="w-5 h-5" />
                </Button>

                {isOptionsMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsOptionsMenuOpen(false)} 
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-surface border border-border rounded-2xl shadow-2xl z-50 py-1.5 animate-scale-in">
                      <button
                        onClick={() => {
                          setIsOptionsMenuOpen(false);
                          if (!lastTransactionId) {
                            toast.error('No se pudo identificar el ID de la transacción para anular.');
                            return;
                          }
                          setIsVoidDrawerOpen(true);
                        }}
                        disabled={isVoided}
                        className="w-full px-4 py-2.5 text-left text-xs font-extrabold text-rose-500 hover:bg-rose-500/10 transition-colors flex items-center gap-2.5 cursor-pointer disabled:opacity-50"
                      >
                        <RotateCcw className="w-4 h-4 text-rose-500 shrink-0" />
                        <span>Anular Venta</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Activity Tab View */}
          {activeTab === 'activity' ? (
            <div className="bg-surface-secondary border border-border p-6 rounded-2xl space-y-4 animate-fade-in">
              <h3 className="font-extrabold text-lg text-text-primary mb-3">Auditoría y Registro de Venta</h3>
              <div className="space-y-4 border-l-2 border-brand-orange/40 pl-4 text-xs">
                <div className="relative">
                  <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <p className="font-bold text-text-primary">Venta procesada y cerrada</p>
                  <p className="text-text-tertiary">{dateStr}</p>
                </div>
                <div className="relative pt-2">
                  <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-brand-sky" />
                  <p className="font-bold text-text-primary">Atención y services atribuidos a esteticistas</p>
                  <p className="text-text-tertiary">Comisiones calculadas automáticamente</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Client Card */}
              {customer && (
                <div className="bg-surface-secondary backdrop-blur-md border border-white/5 p-6 rounded-2xl shadow-xs flex items-center gap-5">
                  <div className="w-14 h-14 bg-brand-orange/10 rounded-full flex items-center justify-center border border-brand-orange/20">
                    <span className="font-black text-brand-orange text-xl">{(customer?.name || 'C').charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <h4 className="font-extrabold text-lg text-text-primary">{customer?.name || 'Cliente'}</h4>
                    <p className="text-sm text-text-secondary mt-0.5">{customer?.email || customer?.phone || 'Sin datos de contacto'}</p>
                  </div>
                </div>
              )}

              {/* Receipt Details */}
              <div className="bg-surface-secondary backdrop-blur-md border border-white/5 p-6 rounded-2xl shadow-xs">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-extrabold text-xl text-white">Detalle de Venta</h3>
                  <span className="text-sm text-text-secondary">{dateStr}</span>
                </div>

                <div className="space-y-4 mb-6">
                  {cartItems.map((item, i) => {
                    const title = item.type === 'package' ? (item.package?.name || 'Combo') : (item.service?.concept || 'Servicio');
                    return (
                      <div key={i} className="flex justify-between items-start pb-4 border-b border-white/5 last:border-0 last:pb-0">
                        <div>
                          <h4 className="font-bold text-base text-text-primary">{title}</h4>
                          <p className="text-sm text-text-secondary mt-1">
                            {item.type === 'package' ? 'Paquete promocional' : `${item.service?.duration_minutes || 30} min`}
                          </p>
                        </div>
                        <span className="font-extrabold text-base">{currencySymbol}{(item.unitPrice * item.quantity).toFixed(2)} {baseCurrency}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="pt-4 border-t border-white/10 space-y-2">
                  <div className="flex justify-between items-center text-sm text-text-secondary">
                    <span>Subtotal Servicios</span>
                    <span className="font-bold text-text-primary">{currencySymbol}{subtotal.toFixed(2)} {baseCurrency}</span>
                  </div>

                  {discount > 0 && (
                    <div className="flex justify-between items-center text-sm text-rose-400">
                      <span>Descuento Global</span>
                      <span className="font-bold">-{currencySymbol}{discount.toFixed(2)} {baseCurrency}</span>
                    </div>
                  )}

                  {tip > 0 && (
                    <div className="flex justify-between items-center text-sm text-emerald-400">
                      <span>Propina Directa ({tipOperator?.name || 'Esteticista'})</span>
                      <span className="font-bold">+{currencySymbol}{tip.toFixed(2)} {baseCurrency}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-2 border-t border-white/10">
                    <span className="font-extrabold text-brand-orange text-lg">Total Pagado</span>
                    <span className="font-black text-white text-2xl">{currencySymbol}{totalCharged.toFixed(2)} {baseCurrency}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Payment History */}
          <div className="bg-surface-secondary backdrop-blur-md border border-white/5 p-6 rounded-2xl shadow-xs space-y-5">
            {paymentEntries.map((entry, i) => {
              const method = paymentMethods.find(m => m.id === entry.paymentMethodId);
              const Icon = getIconForMethod(method?.name || '');
              const isVESMethod = method?.currency === 'VES';
              const amountVES = entry.amount * bcvRate;

              return (
                <div key={i} className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-3 font-bold text-base text-text-primary">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-emerald-500" />
                      </div>
                      Pago {method?.name || 'Desconocido'}
                      {isVESMethod && (
                        <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-brand-sky/10 text-brand-sky border border-brand-sky/20">
                          Bs.S
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary mt-1 ml-11">
                      {dateStr}
                      {entry.reference && ` • Ref: ${entry.reference}`}
                    </p>
                  </div>
                  <div className="text-right">
                    {isVESMethod ? (
                      <>
                        <span className="font-extrabold text-base block text-text-primary">{formatLocal(amountVES)}</span>
                        <span className="text-xs font-semibold text-text-tertiary block">({currencySymbol}{entry.amount.toFixed(2)} {baseCurrency})</span>
                      </>
                    ) : (
                      <span className="font-extrabold text-base block text-text-primary">{currencySymbol}{entry.amount.toFixed(2)} {baseCurrency}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* ── Fixed Footer ── */}
      <div className="bg-surface-secondary/80 backdrop-blur-xl border-t border-white/5 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] lg:pb-6 flex-shrink-0 flex justify-end shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.3)] z-10 relative">
        <Button variant="primary" onClick={() => { reset(); close(); }} className="bg-brand-orange hover:bg-brand-orange/90 transition-colors border-none shadow-lg shadow-brand-orange/20 text-white text-base md:text-lg py-3 px-8 w-full sm:w-auto font-black">
          Cerrar y volver
        </Button>
      </div>

      {/* ── Anular Venta Drawer Modal ── */}
      {isVoidDrawerOpen && lastTransactionId && (
        <VoidTransactionDrawer
          transactionId={lastTransactionId}
          onClose={() => setIsVoidDrawerOpen(false)}
          onVoided={() => setIsVoided(true)}
        />
      )}

    </div>
  );
}
